/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable react/no-danger */
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React, { Fragment, useMemo } from "react"
import notification from "components/common/notification"
import { FormComponentProps, WrappedFormUtils } from "antd/lib/form/Form"
import DOMPurify from "dompurify"
import {
  ModifierGroup,
  Modifier,
  Item,
  OutletStore,
  AdvancedPricing,
  OutletStoreV2,
  MenuTranslationSetting,
  TranslationDetail,
  TranslationSetting,
} from "types/models/menu"
import { Form } from "grab-ui"
import {
  Row,
  Col,
  Drawer,
  Input,
  Radio,
  Button,
  Select,
  Alert,
  List,
  Spin,
  Flex,
} from "@duxton-web/ui"
import { CloseCircleFilled, VerticalDragUniversal } from "@duxton-web/icons"
import { SortableElement } from "react-sortable-hoc"
import arrayMove from "array-move"
import { isNil, isEmpty, isEqual, replace, omit, differenceWith } from "lodash"
import { getUCMConfig } from "utils/config"
import { capitalizeFirstLetter, sleep } from "utils/misc"
import {
  eventValueToCleanNumericInput,
  newDeferredValidator,
  DeferredValidator,
  stringValidations,
} from "utils/form_helper"
import { storageHelper } from "utils/storage"
import { ConfirmModal } from "components/common/ConfirmModal"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventActionEng,
  EventCategory,
  EventCategoryEng,
} from "constants/tracking"
import { MENU_DETAIL } from "constants/routes"
import { MenuState } from "store/duck/menu.type"

import {
  REQUIRED_TO_SELECT,
  OPTIONAL_TO_SELECT,
  exactly,
  inBetween,
} from "utils/modifier_group_helper"
import { OverflowTip, SortableList, TroyIcon } from "components/common"
import { getMustEditThroughMenu } from "hooks/useInventoryContext"
import { RouteComponentProps, withRouter } from "react-router"
import {
  drawerBodyBackground,
  drawerContentSpacingStyle,
  drawerFooter,
  drawerFooterSpacing,
} from "components/drawers/styles"
import InventoryBlockAlert from "components/InventoryBlockAlert"
import { connect } from "react-redux"
import { Dispatch } from "redux"
import { AdditionalPriceDrawer, TranslationDrawer } from "components/drawers"
import {
  AdditionalPrice,
  additionalPriceToItemAdvancedPricing,
  additionalPriceToPriceRange,
  advancedPricingToAdditionalPrice,
} from "constants/service_based_menu"
import useMenuContext from "hooks/useMenuContext"
import PriceInputField from "components/common/forms/PriceInputField"
import { FormItemProps } from "antd/lib/form"
import { minorUnitToCurrency } from "utils/currency"
import useFEConfig from "hooks/useFEConfig"
import { TranslationType } from "constants/menu"
import {
  isAutoTranslationQueueExists,
  translationMandatoryUnfill,
} from "utils/translation_helper"
import { DrawerStyles, listItemWrapper } from "./ModifierGroups.styles"

const ADD = "ADD"
const EDIT = "EDIT"
const ADD_MODIFIERGROUP = "ADD_MODIFIERGROUP"
const EDIT_MODIFIERGROUP = "EDIT_MODIFIERGROUP"

interface SortableItemProps {
  form: WrappedFormUtils<any>
  modifier: Modifier
  outlet?: OutletStore
  outletV2?: OutletStoreV2
  textMap: { [key: string]: string }
  removeCallback: React.MouseEventHandler<HTMLElement>
  editUnable: boolean
  modifyModifierGroupErrorMsg?: any
  changeModifierNameCallback: React.ChangeEventHandler<HTMLInputElement>
  changeAdditionalPriceCallback: (additionalPrice: string) => void
  priceValidator: DeferredValidator
  isModifierDeletable: boolean
  onShowAdditionalPriceDrawer: () => void
  additionalPriceForModifier?: AdditionalPrice
  modifierGroupForDrawer: ModifierGroup
  enableTroyNewMerchantEndpoint?: boolean
  onTargetModifier: (target?: string) => void
  isMenuTranslation: boolean
  isAutoTranslationEnabled: boolean
  merchantID: string
  getTranslation?: (value: TranslationParams) => void
  menuTranslationSetting?: MenuTranslationSetting
}

export interface TranslationParams {
  value: string
  destinationKey: string
  entity: string
  merchantID: string
  menuTranslationSetting?: MenuTranslationSetting
}

export const SortableItem = SortableElement(
  ({
    form,
    modifier,
    outlet,
    outletV2,
    textMap,
    removeCallback,
    editUnable,
    modifyModifierGroupErrorMsg,
    changeModifierNameCallback,
    changeAdditionalPriceCallback,
    priceValidator,
    isModifierDeletable,
    onShowAdditionalPriceDrawer,
    additionalPriceForModifier,
    modifierGroupForDrawer,
    enableTroyNewMerchantEndpoint,
    onTargetModifier,
    isMenuTranslation,
    merchantID,
    getTranslation,
    isAutoTranslationEnabled,
    menuTranslationSetting,
  }: SortableItemProps) => {
    const { getFieldDecorator } = form
    const feConfig = useFEConfig()
    const { isServiceBasedMenuEnabled } = useMenuContext()

    // Only show error after a field is touched.
    const modifierNameError =
      (form.isFieldTouched(`modifierName[${modifier.modifierID}]`) &&
        form.getFieldError(`modifierName[${modifier.modifierID}]`)) ||
      (modifyModifierGroupErrorMsg?.key ===
        `modifierName[${modifier.modifierID}]` &&
      modifyModifierGroupErrorMsg?.message
        ? [modifyModifierGroupErrorMsg?.message]
        : undefined)

    const additionalPriceError =
      form.getFieldError(`additionalPrice[${modifier.modifierID}]`) ||
      (modifyModifierGroupErrorMsg?.key ===
        `additionalPrice[${modifier.modifierID}]` &&
      modifyModifierGroupErrorMsg?.message
        ? [modifyModifierGroupErrorMsg?.message]
        : undefined)
    const formItemProps: FormItemProps = useMemo(
      () => ({
        label: isServiceBasedMenuEnabled
          ? textMap.additionalPrice.toUpperCase()
          : undefined,
        "data-testid": "formitem-mod-additionalprice",
        validateStatus: additionalPriceError ? "error" : "",
        help: additionalPriceError ?? "",
        css: css`
          margin-bottom: 16px;
        `,
      }),
      [
        textMap.additionalPrice,
        additionalPriceError,
        isServiceBasedMenuEnabled,
      ],
    )
    const existingModifier = modifierGroupForDrawer.modifierGroupID
      ? modifierGroupForDrawer.modifiers.find(
          e => e.modifierID === modifier.modifierID,
        )
      : undefined
    const renderPriceInput = () => (
      <PriceInputField
        disabled={editUnable}
        priceValue={
          existingModifier
            ? minorUnitToCurrency(
                existingModifier.priceInMin ?? 0,
                enableTroyNewMerchantEndpoint
                  ? outletV2?.currency?.code
                  : outlet?.currency,
              )
            : null
        }
        outlet={outlet}
        outletV2={outletV2}
        priceValidator={priceValidator}
        onChangePriceInMin={changeAdditionalPriceCallback}
        onPriceRangeClick={onShowAdditionalPriceDrawer}
        fieldID={`additionalPrice[${modifier.modifierID}]`}
        formItemProps={formItemProps}
        form={form}
        priceRange={additionalPriceToPriceRange(additionalPriceForModifier)}
        enableTroyNewMerchantEndpoint={enableTroyNewMerchantEndpoint}
        data-testid="input-mod-additionalprice"
        validationType="modifier"
        required={false}
      />
    )

    const getInputColSpan = () => {
      let span
      if (!editUnable) {
        span = isServiceBasedMenuEnabled ? 20 : 12
      } else {
        span = isServiceBasedMenuEnabled ? 24 : 16
      }
      return span
    }

    const translationMissing = () => {
      if (!isMenuTranslation) {
        return false
      }
      if (
        modifier?.translationError &&
        modifier.translationError.has(modifier.modifierName)
      ) {
        return true
      }
      if (isAutoTranslationEnabled) {
        return false
      }
      return translationMandatoryUnfill(
        menuTranslationSetting?.translationFieldMandatoryLanguages,
        modifier.nameTranslation,
      )
    }

    return (
      <Fragment>
        <Row style={{ zIndex: 9999 }}>
          {!editUnable && (
            <Col span={2}>
              <Form.Item>
                <VerticalDragUniversal className="draggerModifier" />
              </Form.Item>
            </Col>
          )}
          <Col span={getInputColSpan()}>
            <Form.Item
              label={
                isServiceBasedMenuEnabled
                  ? textMap.modifierName.toUpperCase()
                  : undefined
              }
              data-testid="formitem-mod-name"
              validateStatus={modifierNameError ? "error" : ""}
              help={modifierNameError || ""}
              css={css`
                margin-bottom: 16px;
              `}
            >
              {getFieldDecorator(`modifierName[${modifier.modifierID}]`, {
                initialValue: modifier.modifierName,
                rules: [
                  {
                    required: true,
                    message: textMap.requiredField,
                  },
                  {
                    max: getUCMConfig("maxLengthModifierName"),
                    message: replace(
                      textMap.errorCharacterLimit,
                      "$limit",
                      getUCMConfig("maxLengthModifierName"),
                    ),
                  },
                ],
              })(
                <Input
                  data-testid="input-mod-name"
                  placeholder={textMap.modifierName}
                  disabled={editUnable}
                  onChange={e => {
                    changeModifierNameCallback(e)
                    if (isAutoTranslationEnabled) {
                      getTranslation?.({
                        value: e.target.value,
                        destinationKey: `${modifierGroupForDrawer?.modifierGroupID},${modifier.modifierID}`,
                        entity: TranslationType.modifier,
                        merchantID,
                        menuTranslationSetting,
                      })
                    }
                  }}
                />,
              )}
            </Form.Item>
            {isMenuTranslation && (
              <div>
                <span>
                  {textMap.menuTranslationDetailOption.replace(
                    "{x}",
                    menuTranslationSetting?.menuSupportedLanguages.length.toString() ||
                      "",
                  )}
                </span>
                <br />
                <Button
                  type="link"
                  data-testid="name-translation"
                  onClick={() => onTargetModifier(modifier.modifierID)}
                >
                  {textMap.menuTranslationEditTranslation}
                </Button>
                {translationMissing() && (
                  <div
                    css={css`
                      color: #ee6352;
                    `}
                    data-testid="category-translation-error"
                  >
                    {textMap.menuTranslationErrorAutoTranslation}
                    {getFieldDecorator(
                      `modifierTranslation[${modifier.modifierID}]`,
                      {
                        rules: [
                          {
                            required: true,
                            message: textMap.errorRequired,
                          },
                        ],
                      },
                    )(<Input hidden />)}
                  </div>
                )}
              </div>
            )}
            {isServiceBasedMenuEnabled && (
              <Fragment>
                {renderPriceInput()}
                {!feConfig.isLoading ? (
                  feConfig.showAdditionalPriceDrawerLink && (
                    <Button
                      type="link"
                      onClick={onShowAdditionalPriceDrawer}
                      css={css`
                        margin-bottom: 20px;
                      `}
                      disabled={editUnable}
                    >
                      {textMap.additionalPriceLink}
                    </Button>
                  )
                ) : (
                  <Spin />
                )}
              </Fragment>
            )}
          </Col>
          {!isServiceBasedMenuEnabled && (
            <Col span={7} offset={1}>
              {renderPriceInput()}
            </Col>
          )}
          {!editUnable && (
            <Col span={2}>
              <Form.Item>
                <Button
                  data-testid="btn-mod-remove"
                  shape="circle"
                  style={{ border: "none", background: "none" }}
                  onClick={removeCallback}
                  icon={
                    <CloseCircleFilled
                      style={{
                        color:
                          !isModifierDeletable && isServiceBasedMenuEnabled
                            ? "#A0A0A0"
                            : "#F55538",
                      }}
                    />
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Fragment>
    )
  },
)

interface CreateEditParams {
  advancedPricingMap: AdvancedPricingMap
}

interface props extends RouteComponentProps {
  visible: boolean
  linkedItemChange: boolean
  modifyModifierGroupErrorMsg?: {
    key: string
    message: string
  }
  dispatch: Dispatch
  onCloseDrawer: () => void
  onSubmitEditModifierGroup: (createEditParams: CreateEditParams) => void
  onClickLinkItem: () => void
  onSortOrderModifierChange: (newModifiers: Modifier[]) => void
  onAddEmptyModifier: () => void
  onRemoveModifier: (modifierIndex: number) => void
  onChangeModifierGroupRequiredType: (val: string) => void | Promise<void>
  onChangeModifierGroupStartValue: (val: string) => void
  onChangeModifierGroupEndValue: (val: string) => void
  onChangeModifierGroupSelectionRuleChange: (val: number) => void
  onDeleteModifierGroup: () => void
  onCreateNewModifierGroup: (createEditParams: CreateEditParams) => void
  onTogglePreview: (show: boolean) => void
  onChangeModifierName: (index: number, modifierName: string) => void
  onChangeModifierAdditionalPrice: (
    index: number,
    additionalPrice: string,
  ) => void
  onChangeModifierGroupName: (val: string) => void
  onToggleCreateNewModifierGroupClicked: (val: boolean) => void
  onClearModifyModifierGroupErrMsg: () => void
  menu: MenuState
  textMap: any
  createNewModifierGroupClicked: boolean
  linkedItems: Item[]
  modifierGroupForDrawer: ModifierGroup
  // actual modifiers are passed in here
  workingModifiers: Modifier[]
  title: string
  btnTitle: string
  requiredType: string
  isEditAllowed: boolean
  isMartStore?: boolean
  isServiceBasedMenuEnabled?: boolean
  onUpdateModifierTranslation: (value?: TranslationDetail) => void
  onUpdateModifierOptionTranslation: (value: Modifier[]) => void
  onCloseModifierTranslation: (index: number) => void
  onGetTranslation?: (value: TranslationParams) => void
  isAutoTranslationEnabled: boolean
  isMenuTranslation: boolean
  menuTranslationSetting?: MenuTranslationSetting
  autoTranslationQueue?: Set<string>
}

function hasErrors(fieldsError: any) {
  return Object.keys(fieldsError).some(field => {
    // validate nested object for modifier form
    if (typeof fieldsError[field] === "object" && fieldsError[field] !== null) {
      return Object.keys(fieldsError[field]).some(fe => fieldsError[field][fe])
    }
    return fieldsError[field]
  })
}

type AdditionalPriceMap = Record<Modifier["modifierID"], AdditionalPrice>

type AdvancedPricingMap = Record<Modifier["modifierID"], AdvancedPricing>

interface ModifierGroupsDrawerState {
  showRequiredSelectionRulesDetail: boolean
  showOptionalSelectionRulesDetail: boolean
  selectionRulesInfo: string
  selectionRulesError: string
  showDeleteModal: boolean
  madeChanges: boolean
  openedAdditionalPriceDrawerModifierID: string
  additionalPrices: AdditionalPriceMap
  showOptionGroupTranslationDrawer: boolean
  modifierIndex: number
  modifierGroupNameTranslation?: TranslationSetting
  modifierOptionList?: Modifier[]
}
export class ModifierGroupsDrawer extends React.Component<
  props & FormComponentProps,
  ModifierGroupsDrawerState
> {
  private priceValidator: ReturnType<typeof newDeferredValidator>

  private readonly drawerTitle: React.RefObject<any>

  constructor(props: any) {
    super(props)
    this.drawerTitle = React.createRef()
    this.priceValidator = this.makePriceValidator()
  }

  makePriceValidator(): DeferredValidator {
    return newDeferredValidator(this.props.form, [
      {
        pattern: stringValidations.numericalAmountAllowZero,
        message: this.props.textMap.enterNumberBiggerZero,
        ignoreEmptyValue: true,
      },
    ])
  }

  state: ModifierGroupsDrawerState = {
    showRequiredSelectionRulesDetail: false,
    showOptionalSelectionRulesDetail: false,
    selectionRulesInfo: "",
    selectionRulesError: "",
    showDeleteModal: false,
    madeChanges: false,
    openedAdditionalPriceDrawerModifierID: "",
    additionalPrices: {},
    showOptionGroupTranslationDrawer: false,
    modifierIndex: -1,
  }

  updateAdditionalPricesByModifiers = (modifiers: Modifier[]) => {
    this.setState(s => {
      const { additionalPrices } = s
      const newAdditionalPrices: AdditionalPriceMap = {
        ...additionalPrices,
      }
      // set additionalPrices for modifiers which has just existed
      modifiers.forEach(modifier => {
        if (!newAdditionalPrices[modifier.modifierID]) {
          newAdditionalPrices[modifier.modifierID] = {
            regular: String(modifier.priceInMin),
            ...advancedPricingToAdditionalPrice(
              modifier?.advancedPricing,
              this.props.menu.enableTroyNewMerchantEndpoint
                ? this.props.menu.outletV2?.currency?.code
                : this.props.menu.outlet?.currency,
            ),
          }
        }
      })
      return {
        additionalPrices: {
          ...newAdditionalPrices,
        },
      }
    })
  }

  cleanupAdditionalPricesByModifiers = (modifiers: Modifier[]) => {
    this.setState(s => {
      const { additionalPrices } = s
      const newAdditionalPrices: AdditionalPriceMap = omit(
        additionalPrices,
        modifiers.map(m => m.modifierID),
      )
      return {
        additionalPrices: {
          ...newAdditionalPrices,
        },
      }
    })
  }

  componentDidUpdate(prevProps: props & FormComponentProps) {
    this.priceValidator = this.makePriceValidator()
    if (
      // validate when modal drawer is open and
      // modifiers length changed
      (this.props.visible &&
        this.props.workingModifiers?.length !==
          prevProps.workingModifiers?.length) ||
      // codition when click 'create new modifier group'
      (this.props.visible && this.props.createNewModifierGroupClicked)
    ) {
      // delay needed to wait until modifier form
      // component created
      sleep(10).then(() => {
        this.props.form.validateFields()
        this.props.onToggleCreateNewModifierGroupClicked(false)
      })
    }

    // Show some errors to specific form field
    // or show unqualify error to notification
    if (
      !isEqual(
        prevProps.modifyModifierGroupErrorMsg,
        this.props.modifyModifierGroupErrorMsg,
      )
    ) {
      const { modifyModifierGroupErrorMsg } = this.props
      const { setFields, getFieldValue } = this.props.form

      if (modifyModifierGroupErrorMsg?.key === "modifierGroupName") {
        setFields({
          [modifyModifierGroupErrorMsg.key]: {
            value: getFieldValue(modifyModifierGroupErrorMsg.key),
            errors: [new Error(modifyModifierGroupErrorMsg.message)],
          },
        })
      } else if (modifyModifierGroupErrorMsg?.key?.includes("modifierName")) {
        setFields({
          [modifyModifierGroupErrorMsg?.key]: {
            value: getFieldValue(modifyModifierGroupErrorMsg?.key),
            errors: [new Error(modifyModifierGroupErrorMsg.message)],
          },
        })
      } else if (
        modifyModifierGroupErrorMsg?.key?.includes("additionalPrice")
      ) {
        setFields({
          [modifyModifierGroupErrorMsg?.key]: {
            value: getFieldValue(modifyModifierGroupErrorMsg?.key),
            errors: [new Error(modifyModifierGroupErrorMsg.message)],
          },
        })
      } else if (
        modifyModifierGroupErrorMsg?.key?.includes("ExceedChangeLimit")
      ) {
        // @ts-ignore
        this.executeScroll()
      } else if (modifyModifierGroupErrorMsg?.message?.length) {
        notification.error({
          message: modifyModifierGroupErrorMsg?.message,
        })
      }
    }

    // Reset error field (error from BE) when field is touched
    const { key: errorFieldKey } = this.props.modifyModifierGroupErrorMsg ?? {}
    if (errorFieldKey && this.props.form.isFieldTouched(errorFieldKey)) {
      this.props.onClearModifyModifierGroupErrMsg()
      this.props.form.resetFields([errorFieldKey])
    }

    // Flag madeChange state when there is change on
    // linked item
    if (
      prevProps.linkedItemChange !== this.props.linkedItemChange &&
      this.props.linkedItemChange &&
      this.props.visible
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ madeChanges: true })
    }
    // update additionalPrices when modifiers change
    if (
      this.props.visible &&
      // do a quick pre-check here - not very accurate obviously
      prevProps.workingModifiers !== this.props.workingModifiers
    ) {
      const newModifiers = differenceWith(
        this.props.workingModifiers,
        prevProps.workingModifiers,
        (a, b) => a.modifierID === b.modifierID,
      )
      const deletedModifiers = differenceWith(
        prevProps.workingModifiers,
        this.props.workingModifiers,
        (a, b) => a.modifierID === b.modifierID,
      )
      if (newModifiers.length) {
        this.updateAdditionalPricesByModifiers(newModifiers)
      }
      if (deletedModifiers.length) {
        this.cleanupAdditionalPricesByModifiers(deletedModifiers)
      }
    }

    // Menu Translation
    if (
      this.props.modifierGroupForDrawer !== prevProps.modifierGroupForDrawer &&
      (!prevProps.modifierGroupForDrawer?.modifierGroupName ||
        this.props.modifierGroupForDrawer?.nameTranslation?.originalText ===
          this.props.form.getFieldValue("modifierGroupName"))
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        modifierGroupNameTranslation: this.props.modifierGroupForDrawer
          .nameTranslation,
      })
    }
    if (this.props.workingModifiers !== prevProps.workingModifiers) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        modifierOptionList: this.props.workingModifiers,
      })
    }
  }

  handleSelectionRulesChange = (e: any) => {
    const val = e.target.value
    this.props.onChangeModifierGroupSelectionRuleChange(Number(val))
    this.setState({
      selectionRulesInfo: "",
      selectionRulesError: "",
      madeChanges: true,
    })
  }

  handleOnChangeRequiredType = async (val: string) => {
    this.setState({
      selectionRulesError: "",
      madeChanges: true,
    })
    await this.props.onChangeModifierGroupRequiredType(val)
    this.props.form.setFieldsValue({
      requiredValueStart: this.props.modifierGroupForDrawer.selectionRangeMin,
      requiredValueEnd: this.props.modifierGroupForDrawer.selectionRangeMax,
    })
    this.props.form.validateFields()
  }

  checkToSwitchRequiredTypeIfNecessary = (
    startVal: string | number,
    endVal: string | number,
  ) => {
    if (Number(startVal) > 0 && Number(startVal) === Number(endVal)) {
      this.props.form.setFieldsValue({
        requiredType: exactly,
      })
    }
  }

  handleOnChangeStartValue = (e: any) => {
    const val = e.target.value || 0
    this.setState({ madeChanges: true })
    const endVal = this.props.form.getFieldValue("requiredValueEnd")
    this.checkToSwitchRequiredTypeIfNecessary(val, endVal)
    this.props.onChangeModifierGroupStartValue(val)
  }

  handleOnChangeEndValue = (e: any) => {
    const val = e.target.value || 0
    this.setState({ madeChanges: true })
    const startVal = this.props.form.getFieldValue("requiredValueStart")
    this.checkToSwitchRequiredTypeIfNecessary(startVal, val)
    this.props.onChangeModifierGroupEndValue(val)
  }

  handleOnChangeMaximum = (e: any) => {
    const val = e.target.value
    this.setState({ madeChanges: true })
    this.props.onChangeModifierGroupEndValue(val)
    if (!isNil(val)) {
      this.setState({
        selectionRulesInfo: this.props.textMap.selectUpTo.replace(/<x>/i, val),
      })
    }
  }

  handleOnDeleteModifierGroup = () => {
    if (this.props.modifierGroupForDrawer.relatedItemIDs?.length) {
      notification.error({
        message: this.props.textMap.cannotDeleteModifierGroup,
      })
      return
    }
    this.setState({ showDeleteModal: true })
    TroyWebTracker.sendEvent({
      category: EventCategory.menuModifierGroupsTab,
      action: EventAction.clickDeleteModifiergroup,
      labels: {
        ...(this.props.menu.merchantID
          ? { STORE_ID: this.props.menu.merchantID }
          : {
              MERCHANT_GROUP_ID: this.props.menu.merchantGroupID,
              MENU_GROUP_ID: this.props.menu.menuGroupDetail.menuGroupID,
            }),
        MODIFIER_GROUP_ID: this.props.modifierGroupForDrawer?.modifierGroupID,
      },
    })
  }

  handleOnCloseDrawer = () => {
    const {
      onClearModifyModifierGroupErrMsg,
      menu: {
        merchantID,
        menuGroupDetail: { menuGroupID },
        merchantGroupID,
      },
      modifierGroupForDrawer,
    } = this.props
    TroyWebTracker.sendEvent({
      category: EventCategory.menuModifiergroupsModifymodifiergroup,
      action: EventAction.clickExit,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        MODIFIER_GROUP_ID: modifierGroupForDrawer?.modifierGroupID ?? "",
        INTENT: modifierGroupForDrawer?.modifierGroupID
          ? EDIT_MODIFIERGROUP
          : ADD_MODIFIERGROUP,
      },
    })
    this.setState({
      showRequiredSelectionRulesDetail: false,
      showOptionalSelectionRulesDetail: false,
      selectionRulesInfo: "",
      selectionRulesError: "",
      madeChanges: false,
      additionalPrices: {},
    })
    onClearModifyModifierGroupErrMsg()
    this.props.form.resetFields()
    this.props.onCloseDrawer()
  }

  handleConfirmDelete = () => {
    this.setState({
      showDeleteModal: false,
    })
    this.props.onDeleteModifierGroup()
  }

  handleCancel = () => {
    this.setState({
      showDeleteModal: false,
    })
  }

  handleSubmit = () => {
    this.props.form.validateFields((err: any, formValues) => {
      const { additionalPrice } = formValues
      const isValid = Object.keys(additionalPrice).every(additionalPriceKey => {
        const price = additionalPrice[additionalPriceKey]
        return this.priceValidator.validateOnSave(
          price,
          `additionalPrice[${additionalPriceKey}]`,
        )
      })
      if (!isValid) {
        return
      }
      if (!err) {
        const advancedPricingMap = Object.entries(
          this.state.additionalPrices,
        ).reduce<AdvancedPricingMap>(
          (acc, [modifierID, additionalPriceForModifier]) => ({
            ...acc,
            [modifierID]: additionalPriceToItemAdvancedPricing(
              additionalPriceForModifier,
              this.props.menu.enableTroyNewMerchantEndpoint
                ? this.props.menu.outletV2?.currency?.code
                : this.props.menu.outlet?.currency,
            ),
          }),
          {},
        )
        // Create mode
        if (!this.props?.modifierGroupForDrawer?.modifierGroupID) {
          this.props.onCreateNewModifierGroup({
            advancedPricingMap,
          })
        } else {
          // Edit mode
          this.props.onSubmitEditModifierGroup({
            advancedPricingMap,
          })
        }
        this.props.form.resetFields()
      }
    })
  }

  handlePreviewModifierGroup = () => {
    this.props.form.validateFields((err: any) => {
      if (!err) {
        const {
          onTogglePreview,
          menu: {
            merchantID,
            menuGroupDetail: { menuGroupID },
            merchantGroupID,
          },
          modifierGroupForDrawer,
        } = this.props
        onTogglePreview(true)
        TroyWebTracker.sendEvent({
          category: EventCategory.menuModifiergroupsModifymodifiergroup,
          action: EventAction.clickPreviewModifiergroup,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            MODIFIER_GROUP_ID: modifierGroupForDrawer?.modifierGroupID ?? "",
            INTENT: modifierGroupForDrawer?.modifierGroupID
              ? EDIT_MODIFIERGROUP
              : ADD_MODIFIERGROUP,
          },
        })
      }
    })
  }

  handleRemoveModifier = async (index: number) => {
    this.props.onRemoveModifier(index)
    // Modifier group must have at least 1 modifier
    if (this.props.modifierGroupForDrawer?.modifiers?.length !== 1) {
      this.setState({
        madeChanges: true,
      })
    }
    // wait for form item change ready
    await sleep(10)

    this.props.form.validateFields({
      force: true,
    })
  }

  handleLinkItem = (action: string) => {
    const {
      modifierGroupForDrawer,
      menu: {
        merchantID,
        menuGroupDetail: { menuGroupID },
        merchantGroupID,
      },
    } = this.props
    TroyWebTracker.sendEvent({
      category: EventCategory.menuModifiergroupsModifymodifiergroup,
      action:
        action === EDIT
          ? EventAction.clickEditLinkedItem
          : EventAction.clickLinkItem,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        MODIFIER_GROUP_ID: modifierGroupForDrawer?.modifierGroupID ?? "",
        INTENT: modifierGroupForDrawer?.modifierGroupID
          ? EDIT_MODIFIERGROUP
          : ADD_MODIFIERGROUP,
      },
    })
    this.props.onClickLinkItem()
  }

  inBetweenValidator = (rule: any, value: any, callback: any) => {
    const { textMap } = this.props
    const type = this.props.form.getFieldValue("requiredType")
    const startNum = this.props.form.getFieldValue("requiredValueStart") || 0
    const endNum = this.props.form.getFieldValue("requiredValueEnd") || 0

    if (
      !new RegExp(stringValidations.numericalAmount).test(startNum) ||
      (type === inBetween &&
        !new RegExp(stringValidations.numericalAmount).test(endNum))
    ) {
      callback(textMap.enterNumberBiggerZero)
      this.setState({
        selectionRulesError: textMap.enterNumberBiggerZero,
        selectionRulesInfo: "",
      })
    } else if (type === inBetween && Number(startNum) > Number(endNum)) {
      callback(textMap.numberRangeInAscending)
      this.setState({
        selectionRulesError: textMap.numberRangeInAscending,
        selectionRulesInfo: "",
      })
    } else if (
      ((type === exactly || type === inBetween) &&
        Number(startNum) > this.props.workingModifiers?.length) ||
      Number(endNum) > this.props.workingModifiers?.length
    ) {
      callback(textMap.exceedAvailOption)
      this.setState({
        selectionRulesError: textMap.exceedAvailOption,
        selectionRulesInfo: "",
      })
    } else {
      this.setState({ selectionRulesError: "" })
      callback()
    }
  }

  validateMaxValue = (rule: any, value: any, callback: any) => {
    const { textMap } = this.props
    if (
      isNil(value) ||
      value === "" ||
      !new RegExp(stringValidations.numericalAmount).test(value)
    ) {
      callback(textMap.enterNumberBiggerZero)
      this.setState({
        selectionRulesError: textMap.enterNumberBiggerZero,
        selectionRulesInfo: "",
      })
    } else if (Number(value) > this.props.workingModifiers?.length) {
      callback(textMap.exceedAvailOption)
      this.setState({
        selectionRulesError: textMap.exceedAvailOption,
        selectionRulesInfo: "",
      })
    } else {
      this.setState({ selectionRulesError: "" })
      callback()
    }
  }

  onSortEnd = ({ oldIndex, newIndex }: any) => {
    const newModifiers = arrayMove(
      this.props.workingModifiers,
      oldIndex,
      newIndex,
    )
    this.props.onSortOrderModifierChange(newModifiers)
    if (oldIndex !== newIndex) {
      this.setState({ madeChanges: true })
    }
  }

  editUnable = () => {
    return storageHelper.isModifierGroupEditDisabled(
      this.props.modifierGroupForDrawer?.modifierGroupID,
    )
  }

  executeScroll = () => this.drawerTitle.current?.scrollIntoView()

  disabledModifierGroup = () => {
    return storageHelper.getDisabledModifierGroup(
      this.props.modifierGroupForDrawer?.modifierGroupID,
    )
  }

  alertReachEditLimitBody = () => {
    return this.props.textMap[
      `reachEditLimitBody${capitalizeFirstLetter(
        this.disabledModifierGroup()?.range ?? "",
      )}`
    ]
      .replace(/<x>/i, this.disabledModifierGroup()?.limit)
      .replace(/<date.short>/i, this.disabledModifierGroup()?.date)
      .replace(
        /<a>/i,
        `<a href='${storageHelper.getTroyHelpCenterUrlV2()}' target='_blank' rel='noreferrer'>`,
      )
  }

  getExistingDefaultPriceForAdditionalPriceDrawer = () => {
    const modifier = this.props.modifierGroupForDrawer.modifiers.find(
      e => e.modifierID === this.state.openedAdditionalPriceDrawerModifierID,
    )
    return modifier?.priceInMin
      ? minorUnitToCurrency(
          this.props.modifierGroupForDrawer.modifiers.find(
            e =>
              e.modifierID === this.state.openedAdditionalPriceDrawerModifierID,
          )?.priceInMin,
          this.props.menu.enableTroyNewMerchantEndpoint
            ? this.props.menu.outletV2?.currency?.code
            : this.props.menu.outlet?.currency,
        )
      : null
  }

  handleVerifyOptionGroupTranslation = (value?: TranslationDetail) => {
    this.props.form.setFieldsValue({
      nameTranslation: value?.nameTranslation,
    })
    this.setState({
      showOptionGroupTranslationDrawer: false,
      modifierGroupNameTranslation: value?.nameTranslation,
      madeChanges: true,
    })
    this.props.onUpdateModifierTranslation(value)
  }

  handleVerifyOptionTranslation = (value?: TranslationDetail) => {
    if (this.state.modifierOptionList) {
      const updatedOption: Modifier = {
        ...this.state.modifierOptionList[this.state.modifierIndex],
        nameTranslation: value?.nameTranslation,
        resetNameTranslation: undefined,
        translationError: undefined,
      }
      const newModifierList = this.state.modifierOptionList?.map(target =>
        target.modifierID === updatedOption.modifierID
          ? { ...updatedOption }
          : target,
      )

      this.setState({
        modifierIndex: -1,
        modifierOptionList: newModifierList,
        madeChanges: true,
      })
      this.props.onUpdateModifierOptionTranslation(newModifierList)
    }
  }

  handleOnCloseModifierTranslation = () => {
    this.props.onCloseModifierTranslation(this.state.modifierIndex)
    this.setState({
      modifierIndex: -1,
    })
  }

  render() {
    const {
      modifierGroupForDrawer,
      textMap,
      modifyModifierGroupErrorMsg,
      location,
      isServiceBasedMenuEnabled,
      btnTitle,
    } = this.props
    const {
      getFieldDecorator,
      getFieldsError,
      isFieldTouched,
      getFieldError,
      getFieldValue,
    } = this.props.form
    const mustEditThroughMenu = getMustEditThroughMenu(location)
    // Only show error after a field is touched.
    const modifierGroupNameError =
      (isFieldTouched("modifierGroupName") &&
        getFieldError("modifierGroupName")) ||
      (modifyModifierGroupErrorMsg?.key === "modifierGroupName" &&
      modifyModifierGroupErrorMsg?.message
        ? [modifyModifierGroupErrorMsg?.message]
        : undefined)

    // Menu Translation
    const {
      isMenuTranslation,
      isAutoTranslationEnabled,
      menuTranslationSetting,
      autoTranslationQueue,
    } = this.props
    const modifierSelected =
      this.state.modifierIndex >= 0 && this.state.modifierOptionList
        ? this.state.modifierOptionList[this.state.modifierIndex]
        : undefined

    const isAutoTranslationLoadingModifierGroup = isAutoTranslationQueueExists({
      queue: autoTranslationQueue,
      entityType: TranslationType.modifierGroup,
    })
    const isAutoTranslationLoadingModifier = isAutoTranslationQueueExists({
      queue: autoTranslationQueue,
      entityType: TranslationType.modifier,
    })
    const isAutoTranslationLoading =
      isAutoTranslationLoadingModifierGroup || isAutoTranslationLoadingModifier

    const getSaveButtonText = () => {
      if (isAutoTranslationLoading) {
        return textMap.menuTranslationLoadingAutoTranslation
      }
      return btnTitle
    }

    const translationMissing = () => {
      if (!isMenuTranslation) {
        return false
      }
      if (
        modifierGroupForDrawer?.translationError &&
        modifierGroupForDrawer.translationError.has(
          modifierGroupForDrawer.modifierGroupName,
        )
      ) {
        return true
      }
      if (isAutoTranslationEnabled) {
        return false
      }
      return translationMandatoryUnfill(
        menuTranslationSetting?.translationFieldMandatoryLanguages,
        modifierGroupForDrawer.nameTranslation,
      )
    }

    const translationMissingModifiers = () => {
      if (!isMenuTranslation) {
        return false
      }

      const isTranslationError = this.props.workingModifiers.some(
        modifier =>
          modifier.translationError &&
          modifier.translationError.has(modifier.modifierName),
      )
      if (isTranslationError) {
        return true
      }

      if (isAutoTranslationEnabled) {
        return false
      }

      const isTranslationMandatoryUnfill = this.props.workingModifiers.some(
        modifier =>
          translationMandatoryUnfill(
            menuTranslationSetting?.translationFieldMandatoryLanguages,
            modifier.nameTranslation,
          ),
      )
      return isTranslationMandatoryUnfill
    }

    const {
      selectionRangeMax,
      selectionRangeMin,
    } = this.props.modifierGroupForDrawer
    let modifierGroupRule: string = ""
    if (selectionRangeMax === selectionRangeMin) {
      // Require exactly
      modifierGroupRule = textMap.customerCanOnlyChoose.replace(
        /<x>/i,
        selectionRangeMin,
      )
    } else if (
      selectionRangeMin > 0 &&
      selectionRangeMin !== selectionRangeMax
    ) {
      // Require inBetween
      modifierGroupRule = textMap.selectInBetween
        .replace(/<x>/i, selectionRangeMin)
        .replace(/<y>/i, selectionRangeMax)
    } else if (selectionRangeMin === 0 && selectionRangeMax > 0) {
      // Optional with maximum selection
      modifierGroupRule = textMap.selectUpTo.replace(/<x>/i, selectionRangeMax)
    }

    const fieldEditUnable =
      this.editUnable() || mustEditThroughMenu || !this.props.isEditAllowed

    const renderAddNewModifierButton = () =>
      !fieldEditUnable && (
        <span
          data-testid="btn-modgroup-addmod"
          className="addNewModifierButton"
          onClick={async () => {
            await this.props.onAddEmptyModifier()
            this.props.form.validateFields({
              force: true,
            })
          }}
        >
          {textMap.addNewModifier}
        </span>
      )

    return (
      <Fragment>
        <Drawer
          data-testid="drawer-modgroup"
          title={<span ref={this.drawerTitle}>{`${this.props.title}`}</span>}
          width={450}
          onClose={this.handleOnCloseDrawer}
          open={this.props.visible}
          css={DrawerStyles}
          destroyOnClose
        >
          <Form>
            {this.editUnable() && (
              <Row className="pad-24">
                <Col span={24}>
                  <Alert
                    message={
                      <p>
                        {
                          textMap[
                            `reachEditLimitTitle${capitalizeFirstLetter(
                              this.disabledModifierGroup()?.range ?? "",
                            )}`
                          ]
                        }
                      </p>
                    }
                    description={
                      <p
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            this.alertReachEditLimitBody(),
                          ),
                        }}
                      />
                    }
                    type="error"
                    showIcon
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col span={24}>
                <Form.Item
                  data-testid="formitem-modgroup-name"
                  label={textMap.modifierGroupName.toUpperCase()}
                  className={
                    this.editUnable()
                      ? "name-form-item-no-top-margin"
                      : "name-form-item"
                  }
                  validateStatus={modifierGroupNameError ? "error" : ""}
                  help={modifierGroupNameError || ""}
                >
                  {getFieldDecorator("modifierGroupName", {
                    initialValue: modifierGroupForDrawer.modifierGroupName,
                    rules: [
                      {
                        required: true,
                        message: textMap.requiredField,
                      },
                      {
                        max: getUCMConfig("maxLengthModifierGroupName"),
                        message: replace(
                          textMap.errorCharacterLimit,
                          "$limit",
                          getUCMConfig("maxLengthModifierGroupName"),
                        ),
                      },
                    ],
                  })(
                    <Input
                      data-testid="input-modgroup-name"
                      placeholder={textMap.modifierGroupName}
                      onChange={e => {
                        this.props.onChangeModifierGroupName(e.target.value)
                        this.setState({
                          madeChanges: true,
                        })
                        if (isAutoTranslationEnabled) {
                          this.props.onGetTranslation?.({
                            value: e.target.value,
                            destinationKey:
                              modifierGroupForDrawer.modifierGroupID,
                            entity: TranslationType.modifierGroup,
                            merchantID: this.props.menu.merchantID,
                            menuTranslationSetting,
                          })
                        }
                      }}
                      disabled={fieldEditUnable}
                    />,
                  )}
                </Form.Item>
              </Col>
            </Row>
            {isMenuTranslation && (
              <Row>
                <Col span={24}>
                  <div className="translationWrapper">
                    <span>
                      {textMap.menuTranslationDetailOptionGroup.replace(
                        "{x}",
                        menuTranslationSetting?.menuSupportedLanguages.length.toString(),
                      )}
                    </span>
                    <br />
                    <Button
                      type="link"
                      data-testid="name-translation"
                      onClick={() => {
                        TroyWebTracker.sendEvent({
                          category: EventCategoryEng.showTranslationDrawer,
                          action: EventActionEng.showModifierTranslation,
                        })
                        this.setState({
                          showOptionGroupTranslationDrawer: true,
                        })
                      }}
                    >
                      {textMap.menuTranslationEditTranslation}
                    </Button>
                    {translationMissing() && (
                      <div
                        css={css`
                          color: #ee6352;
                        `}
                        data-testid="category-translation-error"
                      >
                        {textMap.menuTranslationErrorAutoTranslation}
                      </div>
                    )}
                  </div>
                  {getFieldDecorator("nameTranslation", {
                    initialValue: modifierGroupForDrawer.nameTranslation,
                  })}
                </Col>
              </Row>
            )}
            <Row>
              <Col span={24}>
                <div className="divider">
                  <Row justify="space-between">
                    <Col>
                      <span
                        style={{
                          textTransform: "uppercase",
                        }}
                      >
                        {textMap.modifiers}
                      </span>
                    </Col>
                    {isServiceBasedMenuEnabled && (
                      <Col>{renderAddNewModifierButton()}</Col>
                    )}
                  </Row>
                </div>
              </Col>
            </Row>
            <div className="modifiersWrapper">
              {!isServiceBasedMenuEnabled && (
                <Row>
                  {!fieldEditUnable && <Col span={2} />}
                  <Col className="nameLabel" span={!fieldEditUnable ? 12 : 16}>
                    {textMap.modifierName}
                  </Col>
                  <Col className="additionalPriceLabel" span={7} offset={1}>
                    {textMap.additionalPrice}
                  </Col>
                </Row>
              )}
              <SortableList
                data-testid="modgroup-sortablelist"
                onSortEnd={this.onSortEnd}
                lockAxis="y"
                shouldCancelStart={fieldEditUnable ? () => true : undefined}
              >
                {this.props.workingModifiers.map(
                  (modifier: Modifier, index: number) => (
                    <SortableItem
                      key={modifier.modifierID}
                      modifierGroupForDrawer={modifierGroupForDrawer}
                      index={index}
                      form={this.props.form}
                      textMap={textMap}
                      modifier={modifier}
                      outlet={this.props.menu.outlet ?? undefined}
                      outletV2={this.props.menu.outletV2 ?? undefined}
                      enableTroyNewMerchantEndpoint={
                        this.props.menu.enableTroyNewMerchantEndpoint
                      }
                      editUnable={fieldEditUnable}
                      modifyModifierGroupErrorMsg={
                        this.props.modifyModifierGroupErrorMsg
                      }
                      removeCallback={() => this.handleRemoveModifier(index)}
                      changeModifierNameCallback={(e: any) => {
                        this.props.onChangeModifierName(index, e.target.value)
                        this.setState({
                          madeChanges: true,
                        })
                      }}
                      changeAdditionalPriceCallback={additionalPrice => {
                        this.props.onChangeModifierAdditionalPrice(
                          index,
                          additionalPrice,
                        )
                        this.setState({
                          madeChanges: true,
                        })
                        // update regular price in additionalPrices when modifier priceInMin updated
                        const newAdditionalPrices = {
                          ...this.state.additionalPrices,
                        }
                        if (!newAdditionalPrices[modifier.modifierID]) {
                          newAdditionalPrices[modifier.modifierID] = {
                            regular: null,
                          }
                        }
                        newAdditionalPrices[modifier.modifierID] = {
                          regular: additionalPrice,
                        }
                        this.setState({
                          additionalPrices: newAdditionalPrices,
                        })
                      }}
                      priceValidator={this.priceValidator}
                      onShowAdditionalPriceDrawer={() =>
                        this.setState({
                          openedAdditionalPriceDrawerModifierID:
                            modifier.modifierID,
                        })
                      }
                      isModifierDeletable={
                        this.props.workingModifiers.length > 1
                      }
                      additionalPriceForModifier={
                        this.state.additionalPrices[modifier.modifierID]
                      }
                      onTargetModifier={() => {
                        TroyWebTracker.sendEvent({
                          category: EventCategoryEng.showTranslationDrawer,
                          action: EventActionEng.showModifierTranslation,
                        })
                        this.setState({
                          modifierIndex: index,
                        })
                      }}
                      isMenuTranslation={isMenuTranslation}
                      isAutoTranslationEnabled={isAutoTranslationEnabled}
                      merchantID={this.props.menu.merchantID}
                      getTranslation={this.props.onGetTranslation}
                      menuTranslationSetting={menuTranslationSetting}
                    />
                  ),
                )}
              </SortableList>
              {!isServiceBasedMenuEnabled && renderAddNewModifierButton()}
            </div>
            <Row className="divider">
              <Col span={24}>
                <div>
                  <span
                    style={{
                      textTransform: "uppercase",
                    }}
                  >
                    {textMap.selectionRules}
                  </span>
                </div>
              </Col>
            </Row>
            <Row className="selection-rule-content">
              <Col span={24}>
                {getFieldDecorator("selectionRules", {
                  initialValue:
                    selectionRangeMin >= 1
                      ? REQUIRED_TO_SELECT
                      : OPTIONAL_TO_SELECT,
                })(
                  <Radio.Group
                    onChange={this.handleSelectionRulesChange}
                    data-testid="radiogroup-selection"
                    disabled={fieldEditUnable}
                  >
                    <Radio
                      css={css`
                        margin-bottom: 14px;
                      `}
                      value={REQUIRED_TO_SELECT}
                    >
                      {textMap.requiredToSelect}
                    </Radio>
                    {selectionRangeMin >= 1 && (
                      <Row
                        css={css`
                          margin: 0 0 14px 24px;
                        `}
                      >
                        <Col
                          span={this.props.requiredType === exactly ? 12 : 11}
                        >
                          {getFieldDecorator("requiredType", {
                            initialValue:
                              selectionRangeMin < selectionRangeMax
                                ? inBetween
                                : exactly,
                          })(
                            <Select
                              style={{
                                width: 160,
                              }}
                              data-testid="select-requiredtype"
                              className="form-select"
                              onChange={this.handleOnChangeRequiredType}
                              disabled={fieldEditUnable}
                            >
                              <Select.Option value={exactly}>
                                {textMap.exactly}
                              </Select.Option>
                              <Select.Option value={inBetween}>
                                {textMap.inBetween}
                              </Select.Option>
                            </Select>,
                          )}
                        </Col>
                        <Col span={4}>
                          {getFieldDecorator("requiredValueStart", {
                            initialValue: selectionRangeMin,
                            rules: [
                              {
                                validator: this.inBetweenValidator,
                              },
                            ],
                            getValueFromEvent: eventValueToCleanNumericInput,
                          })(
                            <Input
                              data-testid="input-requiredvaluestart"
                              onChange={this.handleOnChangeStartValue}
                              disabled={fieldEditUnable}
                            />,
                          )}
                        </Col>
                        {this.props.requiredType === inBetween ? (
                          <Col span={1} className="dash">
                            -
                          </Col>
                        ) : null}
                        {this.props.requiredType === inBetween ? (
                          <Col span={4}>
                            {getFieldDecorator("requiredValueEnd", {
                              initialValue: selectionRangeMax,
                              rules: [
                                {
                                  validator: this.inBetweenValidator,
                                },
                              ],
                              getValueFromEvent: eventValueToCleanNumericInput,
                            })(
                              <Input
                                onChange={this.handleOnChangeEndValue}
                                data-testid="input-requiredvalueend"
                                disabled={fieldEditUnable}
                              />,
                            )}
                          </Col>
                        ) : null}
                      </Row>
                    )}
                    <Radio value={OPTIONAL_TO_SELECT}>
                      {textMap.optionalForCustomer}
                    </Radio>

                    {/* Optional for customer to select */}
                    {selectionRangeMin === 0 && (
                      <Row
                        style={{
                          marginLeft: "22px",
                        }}
                      >
                        <Col span={5}>
                          <Form.Item
                            label={textMap.upTo.toUpperCase()}
                            className="maximumInput"
                            help={null}
                            // eslint-disable-next-line react/jsx-curly-brace-presence
                            validateStatus={""}
                          >
                            {getFieldDecorator("maximum", {
                              initialValue: selectionRangeMax,
                              rules: [
                                {
                                  validator: this.validateMaxValue,
                                },
                              ],
                              getValueFromEvent: eventValueToCleanNumericInput,
                            })(
                              <Input
                                data-testid="input-maximum"
                                onChange={this.handleOnChangeMaximum}
                                disabled={fieldEditUnable}
                              />,
                            )}
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </Radio.Group>,
                )}
              </Col>
            </Row>
            <Row className="selection-rule-info">
              <Col span={24}>
                {modifierGroupRule && !this.state.selectionRulesError && (
                  <Alert message={modifierGroupRule} type="info" showIcon />
                )}
                {this.state.selectionRulesError && (
                  <Alert
                    message={this.state.selectionRulesError}
                    data-testid="alert-selection-error"
                    type="error"
                    showIcon
                  />
                )}
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <div className="divider">
                  <span
                    style={{
                      textTransform: "uppercase",
                    }}
                  >
                    {textMap.linkedItems}
                  </span>
                  {!isEmpty(this.props.linkedItems) && !fieldEditUnable && (
                    <span
                      className="editItemBtn"
                      onClick={() => this.handleLinkItem(EDIT)}
                      data-testid="btn-edit-item"
                    >
                      {textMap.edit}
                    </span>
                  )}
                </div>
              </Col>
            </Row>
            {isEmpty(this.props.linkedItems) && (
              <Row
                className="pad-24 pad-btm-80 emptyLinkedItemWrapper"
                justify="center"
              >
                <Col span={20}>
                  <div
                    style={{
                      textAlign: "center",
                    }}
                  >
                    <Flex justify="center">
                      <TroyIcon
                        css={css`
                          margin-top: 27px;
                          margin-bottom: 14.13px;
                        `}
                        type={this.props.isMartStore ? "martCart" : "bag"}
                      />
                    </Flex>
                    <p>{textMap.emptyLinkedItems}</p>
                    <Flex justify="center">
                      <Button
                        data-testid="btn-add-item"
                        onClick={() => this.handleLinkItem(ADD)}
                        disabled={fieldEditUnable}
                      >
                        {textMap.linkItem}
                      </Button>
                    </Flex>
                  </div>
                </Col>
              </Row>
            )}
            {!isEmpty(this.props.linkedItems) && (
              <Row
                className={
                  modifierGroupForDrawer?.modifierGroupID
                    ? "linked-item-wrapper"
                    : "linked-item-wrapper-pad-btm"
                }
                css={listItemWrapper}
              >
                <Col span={24}>
                  <List
                    itemLayout="horizontal"
                    dataSource={this.props.linkedItems}
                    data-testid="list-linked-items"
                    renderItem={(item: Item) => (
                      <List.Item>
                        <List.Item.Meta
                          description={
                            <div className="listItemWrapper">
                              <OverflowTip data-testid="list-linked-items-name">
                                {item.itemName}
                              </OverflowTip>
                              <span data-testid="list-linked-items-pricedisplay">
                                {isServiceBasedMenuEnabled
                                  ? item.priceRange
                                  : item.priceDisplay}
                              </span>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </Col>
              </Row>
            )}
            {mustEditThroughMenu && (
              <Row
                css={css`
                  ${drawerContentSpacingStyle}
                  ${drawerBodyBackground}
                  padding-bottom: 0;
                `}
              >
                <InventoryBlockAlert
                  merchantID={
                    this.props.menu.enableTroyNewMerchantEndpoint
                      ? this.props.menu.outletV2?.ID!
                      : this.props.menu.outlet?.merchantID!
                  }
                  merchantName={
                    this.props.menu.enableTroyNewMerchantEndpoint
                      ? this.props.menu.outletV2?.name!
                      : this.props.menu.outlet?.merchantName!
                  }
                  redirectRoute={MENU_DETAIL({
                    merchantID: this.props.menu.enableTroyNewMerchantEndpoint
                      ? this.props.menu.outletV2?.ID
                      : this.props.menu.outlet?.merchantID,
                    tabKey: "modifierGroups",
                    itemID: modifierGroupForDrawer.modifierGroupID,
                  })}
                />
              </Row>
            )}
            {!isEmpty(modifierGroupForDrawer.modifierGroupID) &&
              !mustEditThroughMenu && (
                <Row className="deleteModifierSection">
                  <Col span={24}>
                    <Button
                      data-testid="btn-modgroup-delete"
                      danger
                      block
                      onClick={this.handleOnDeleteModifierGroup}
                      disabled={!this.props.isEditAllowed}
                    >
                      {textMap.deleteModifierGroup}
                    </Button>
                  </Col>
                </Row>
              )}
          </Form>
          <div
            css={css`
              ${drawerFooterSpacing}
              ${drawerBodyBackground}
            `}
          />
          <div css={drawerFooter}>
            <Row gutter={12}>
              {!!this.props.linkedItems.length && (
                <Col span={6}>
                  <Button
                    block
                    data-testid="btn-modgroup-preview"
                    onClick={this.handlePreviewModifierGroup}
                  >
                    {textMap.preview}
                  </Button>
                </Col>
              )}
              <Col flex="auto">
                <Button
                  type="primary"
                  data-testid="btn-submit-modgroup"
                  block
                  onClick={this.handleSubmit}
                  disabled={
                    hasErrors(getFieldsError()) ||
                    translationMissing() ||
                    translationMissingModifiers() ||
                    fieldEditUnable ||
                    !this.props.isEditAllowed ||
                    (!!this.props.modifierGroupForDrawer.modifierGroupID &&
                      !this.state.madeChanges)
                  }
                  loading={isAutoTranslationLoading}
                >
                  {getSaveButtonText()}
                </Button>
              </Col>
            </Row>
          </div>
        </Drawer>
        <ConfirmModal
          visible={this.state.showDeleteModal}
          title={textMap.confirmDeleteModifierGroupTitle}
          onCancel={this.handleCancel}
          onConfirm={this.handleConfirmDelete}
          content={textMap.confirmDeleteModifierGroupBody.replace(
            /<x>/i,
            this.props.linkedItems.length,
          )}
        />
        {isServiceBasedMenuEnabled && (
          <AdditionalPriceDrawer
            visible={this.state.openedAdditionalPriceDrawerModifierID !== ""}
            onClose={() => {
              this.setState({
                openedAdditionalPriceDrawerModifierID: "",
              })
            }}
            existingDefaultPrice={this.getExistingDefaultPriceForAdditionalPriceDrawer()}
            onAdditionalPriceChange={additionalPrice => {
              const newAdditionalPrices = {
                ...this.state.additionalPrices,
              }
              newAdditionalPrices[
                this.state.openedAdditionalPriceDrawerModifierID
              ] = additionalPrice
              this.setState({
                additionalPrices: newAdditionalPrices,
              })
              // update original additionalPrice with drawer regular price
              const updatedPriceInMinFieldKey = `additionalPrice[${this.state.openedAdditionalPriceDrawerModifierID}]`
              this.props.form.setFieldsValue({
                [updatedPriceInMinFieldKey]: additionalPrice.regular || "",
              })
              this.props.onChangeModifierAdditionalPrice(
                this.props.workingModifiers.findIndex(
                  e =>
                    e.modifierID ===
                    this.state.openedAdditionalPriceDrawerModifierID,
                ),
                additionalPrice.regular || "",
              )
              this.setState({
                madeChanges: true,
              })
            }}
            validationType="modifier"
            initialValues={
              this.state.additionalPrices[
                this.state.openedAdditionalPriceDrawerModifierID
              ]
            }
          />
        )}
        {isMenuTranslation && (
          <TranslationDrawer
            data-testid="translation-option-group-drawer"
            translationType={TranslationType.modifierGroup}
            visible={this.state.showOptionGroupTranslationDrawer}
            merchantID={this.props.menu.merchantID}
            merchantGroupID={this.props.menu.merchantGroupID}
            menuGroupID={this.props.menu.menuGroupDetail.menuGroupID}
            menuTranslationSetting={menuTranslationSetting}
            nameTranslation={this.state.modifierGroupNameTranslation}
            resetNameTranslation={modifierGroupForDrawer?.resetNameTranslation}
            defaultValue={getFieldValue("modifierGroupName")}
            destinationKey={modifierGroupForDrawer?.modifierGroupID}
            onClose={() =>
              this.setState({ showOptionGroupTranslationDrawer: false })
            }
            onSave={this.handleVerifyOptionGroupTranslation}
            disabled={fieldEditUnable}
            isAutoTranslationEnabled={isAutoTranslationEnabled}
            autoTranslationQueue={autoTranslationQueue}
          />
        )}
        {isMenuTranslation && (
          <TranslationDrawer
            data-testid="translation-option-drawer"
            translationType={TranslationType.modifier}
            visible={this.state.modifierIndex >= 0}
            merchantID={this.props.menu.merchantID}
            merchantGroupID={this.props.menu.merchantGroupID}
            menuGroupID={this.props.menu.menuGroupDetail.menuGroupID}
            menuTranslationSetting={menuTranslationSetting}
            nameTranslation={modifierSelected?.nameTranslation}
            resetNameTranslation={modifierSelected?.resetNameTranslation}
            destinationKey={`${modifierGroupForDrawer?.modifierGroupID},${modifierSelected?.modifierID}`}
            defaultValue={modifierSelected?.modifierName || ""}
            onClose={this.handleOnCloseModifierTranslation}
            onSave={this.handleVerifyOptionTranslation}
            disabled={fieldEditUnable}
            isAutoTranslationEnabled={isAutoTranslationEnabled}
            autoTranslationQueue={autoTranslationQueue}
          />
        )}
      </Fragment>
    )
  }
}

export const ModifierGroupsDrawerForm = connect(null, dispatch => ({
  dispatch,
}))(withRouter(Form.create<props & FormComponentProps>()(ModifierGroupsDrawer)))
